import { Component, signal } from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { OfferPaymentAgreementNode } from '@modules/campaigns/Domain/Nodes/OfferPaymentAgreement';
import { OfferPaymentAgreementConfig } from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { DividerModule } from 'primeng/divider';
import { PipeModule } from '@shared/pipes/pipe.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormsModule } from '@angular/forms';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessagesModule } from 'primeng/messages';
import { InspectorBase } from '@shared/components/joint-js/components/inspectors/inspector-base';

type ExpirationMode = 'days';

@Component({
  selector: 'app-offer-payment-agreement-inspector',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    DividerModule,
    PipeModule,
    TranslateModule,
    SelectButtonModule,
    FormsModule,
    InputNumberModule,
    MessagesModule,
  ],
  templateUrl: './offer-payment-agreement-inspector.component.html',
  styleUrls: ['./offer-payment-agreement-inspector.component.scss'],
})
export class OfferPaymentAgreementInspectorComponent extends InspectorBase<OfferPaymentAgreementNode> {
  expirationMode = signal<ExpirationMode>('days');
  expirationDays = signal<number | null>(1);
  numberOfInstalments = signal<number | null>(2);

  constructor(
    private workflow: WorkflowStateService,
    private translate: TranslateService,
  ) {
    super();
  }

  override get nodeValue(): OfferPaymentAgreementNode | null {
    const current = this.node();
    if (!current) return null;
    return current.type === NodeType.OfferPaymentAgreement
      ? (current as OfferPaymentAgreementNode)
      : null;
  }

  getTabs() {
    return [
      {
        label: this.translate.instant(
          'DATA.MODULES.INSPECTORS.OFFER_PAYMENT_AGREEMENT.TABS.PREVIEW',
        ),
        value: 'preview' as const,
      },
      {
        label: this.translate.instant(
          'DATA.MODULES.INSPECTORS.OFFER_PAYMENT_AGREEMENT.TABS.CONFIG',
        ),
        value: 'config' as const,
      },
    ];
  }

  get previewExpirationText(): string {
    const days = this.expirationDays();
    if (days == null) return '-';

    return `${days} ${
      days === 1
        ? this.translate.instant('DATA.COMMON.TIME.DAY')
        : this.translate.instant('DATA.COMMON.TIME.DAYS')
    }`;
  }

  get previewInstalmentsText(): string {
    const instalments = this.numberOfInstalments();
    if (instalments == null) return '-';
    return `${instalments} ${
      instalments === 1
        ? this.translate.instant('DATA.MODULES.CAMPAINGS.ELEMENTS.NODES.PARAMETERS.FEE')
        : this.translate.instant('DATA.MODULES.CAMPAINGS.ELEMENTS.NODES.PARAMETERS.INSTALLMENTS')
    }`;
  }

  override applyChanges() {
    if (this.mode() === 'preview') return;

    const node = this.nodeValue;
    if (!node) return;

    const updatedConfig: OfferPaymentAgreementConfig = {
      ...(node.config as OfferPaymentAgreementConfig),
      expirationDays: this.expirationDays(),
      numberOfInstalments: this.numberOfInstalments(),
    };

    const updated = node.cloneWithConfig(updatedConfig) as OfferPaymentAgreementNode;
    const validation = updated.validate();
    this.errors.set(validation);

    if (!validation.length) {
      this.workflow.addOrUpdateNode(updated);
    }
  }

  onExpirationDaysChange(value: number | null) {
    if (value == null || isNaN(value as any)) value = 0;
    if (value < 0) value = 0;
    if (value > 90) value = 90;

    this.expirationDays.set(value);
    this.expirationMode.set('days');
    this.scheduleApplyChanges();
  }

  onInstalmentsChange(value: number | null) {
    if (value == null || isNaN(value as any)) value = 2;
    if (value < 1) value = 1;
    if (value > 15) value = 15;

    this.numberOfInstalments.set(value);
    this.scheduleApplyChanges();
  }

  override refreshFromConfig() {
    const node = this.nodeValue;
    if (!node) return;

    const cfg = (node.config ?? {}) as OfferPaymentAgreementConfig;

    this.expirationDays.set(cfg.expirationDays ?? 1);
    this.numberOfInstalments.set(cfg.numberOfInstalments ?? 2);

    this.expirationMode.set('days');
    this.errors.set([]);
  }
}
